import React, { createContext, useContext, useMemo, useRef, useState, useCallback } from 'react'
import axios from 'axios'
import { requestHandler } from '@/app/utils/requestHandler'
import { useToast } from '@/app/context/toast-provider'
import type { GraphDataResult } from '@/lib/predefined-graphs/types'

interface MonthlyDashboardData {
    hiresComparison: { headers: string[]; rows: any[][] }
    timeToOfferComparison: { headers: string[]; rows: any[][] }
    topRecruitersAndHiringManagers: { headers: string[]; rows: any[][] }
    recruiterRoles: { labels: string[]; series: { name: string; data: number[] }[] }
}

interface CustomChartResponse {
    id: number
    type: 'card' | 'bar' | 'donut' | 'pie' | 'table' | 'stackedBar'
    title: string
    chartData?: any
    tableData?: { headers: string[]; rows: any[][] }
    totalRows?: number
    placementOrder?: number
    dashboardId?: string
    assignmentGroupId?: string
    sourceGraphKey?: string
}

interface DashboardChartsContextValue {
    customCharts: CustomChartResponse[]
    dashboardTabs: string[]
    dashboardNames: Record<string, string>
    loadingCustomCharts: boolean
    predefinedGraphsData: Record<string, GraphDataResult>
    loadingPredefinedGraphs: boolean
    monthlyDashboardData: MonthlyDashboardData | null
    loadingMonthlyDashboard: boolean
    statusMetrics: Record<string, number> | null
    loadingStatusMetrics: boolean
    fetchCustomCharts: (force?: boolean, dateRange?: { start: string; end: string }) => Promise<void>
    fetchPredefinedGraphs: (sectionId: string, start: string, end: string, force?: boolean) => Promise<void>
    fetchMonthlyDashboard: (month: string, force?: boolean) => Promise<void>
    fetchStatusMetrics: (companySlug: string, start: string, end: string, force?: boolean) => Promise<void>
    invalidatePredefinedCache: (sectionId?: string, start?: string, end?: string) => void
    addDashboardTab: (id: string) => void
    renameDashboard: (dashboardId: string, name: string) => Promise<boolean>
    deleteDashboard: (dashboardId: string) => Promise<boolean>
}

const DashboardChartsContext = createContext<DashboardChartsContextValue | undefined>(undefined)

export const DashboardChartsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { showErrorToast } = useToast()
    const [customCharts, setCustomCharts] = useState<CustomChartResponse[]>([])
    const [dashboardTabs, setDashboardTabs] = useState<string[]>([])
    const [dashboardNames, setDashboardNames] = useState<Record<string, string>>({})
    const [loadingCustomCharts, setLoadingCustomCharts] = useState(false)
    const [predefinedGraphsData, setPredefinedGraphsData] = useState<Record<string, GraphDataResult>>({})
    const [loadingPredefinedGraphs, setLoadingPredefinedGraphs] = useState(false)
    const [monthlyDashboardData, setMonthlyDashboardData] = useState<MonthlyDashboardData | null>(null)
    const [loadingMonthlyDashboard, setLoadingMonthlyDashboard] = useState(false)
    const [statusMetrics, setStatusMetrics] = useState<Record<string, number> | null>(null)
    const [loadingStatusMetrics, setLoadingStatusMetrics] = useState(false)

    const customChartsCache = useRef<Map<string, { charts: CustomChartResponse[]; dashboardTabs: string[]; dashboardNames: Record<string, string> }>>(new Map())
    const predefinedDataCache = useRef<Map<string, Record<string, GraphDataResult>>>(new Map())
    const monthlyDataCache = useRef<Map<string, MonthlyDashboardData>>(new Map())
    const statusMetricsCache = useRef<Map<string, Record<string, number>>>(new Map())

    const fetchCustomCharts = useCallback(async (force = false, dateRange?: { start: string; end: string }) => {
        const defaultStart = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().slice(0, 10)
        const defaultEnd = new Date().toISOString().slice(0, 10)
        const start = dateRange?.start || defaultStart
        const end = dateRange?.end || defaultEnd
        const key = `${start}:${end}`

        if (!force) {
            const cached = customChartsCache.current.get(key)
            if (cached) {
                setDashboardNames(cached.dashboardNames)
                setCustomCharts(cached.charts)
                setDashboardTabs(cached.dashboardTabs)
                return
            }
        }
        const processData = (data: any) => {
            const charts = (data?.customCharts || []) as CustomChartResponse[]
            const rawDashboards = (data?.dashboards || []) as { dashboardId: string; name: string }[]
            const namesMap = rawDashboards.reduce((acc: Record<string, string>, d: any) => {
                if (d?.dashboardId && d?.name) acc[d.dashboardId] = d.name
                return acc
            }, {})
            setDashboardNames(namesMap)
            const sortedCharts = charts.sort((a, b) => (a.placementOrder ?? 0) - (b.placementOrder ?? 0))
            const normalizedCharts = sortedCharts.map((chart) => {
                const chartDashboardId = chart.dashboardId || 'my_dashboard'
                const normalizedDashboardId = chartDashboardId === 'my_dashboard' ? 'default_section' : chartDashboardId
                return { ...chart, dashboardId: normalizedDashboardId }
            })
            setCustomCharts(normalizedCharts)
            // Get dashboard IDs from both charts and dashboard configuration
            const chartDashboardIds = normalizedCharts.map((chart) => chart.dashboardId || 'default_section')
            const configDashboardIds = rawDashboards.map((d: any) => {
                const dashboardId = d.dashboardId || 'my_dashboard'
                return dashboardId === 'my_dashboard' ? 'default_section' : dashboardId
            })
            const allDashboardIds = new Set([...chartDashboardIds, ...configDashboardIds, 'default_section'])
            const dashboards = Array.from(allDashboardIds)
            setDashboardTabs(dashboards)
            customChartsCache.current.set(key, {
                charts: normalizedCharts,
                dashboardTabs: dashboards,
                dashboardNames: namesMap,
            })
        }
        await requestHandler(
            () => axios.get('/api/metrics', { params: { start, end } }),
            processData,
            () => {},
            setLoadingCustomCharts
        )
    }, [])

    const fetchPredefinedGraphs = useCallback(async (sectionId: string, start: string, end: string, force = false) => {
        const key = `${sectionId}:${start}:${end}`
        const cached = predefinedDataCache.current.get(key)
        if (!force && cached) {
            setPredefinedGraphsData(cached)
            return
        }
        await requestHandler(
            () =>
                axios.post('/api/insights/predefined-graphs', {
                    sectionId,
                    dateRange: { start, end },
                }),
            (data) => {
                if (data?.data) {
                    const graphData = data.data as Record<string, GraphDataResult>
                    predefinedDataCache.current.set(key, graphData)
                    setPredefinedGraphsData(graphData)
                }
            },
            () => {},
            setLoadingPredefinedGraphs
        )
    }, [])

    const fetchMonthlyDashboard = useCallback(async (month: string, force = false) => {
        if (!month) return
        const cached = monthlyDataCache.current.get(month)
        if (!force && cached) {
            setMonthlyDashboardData(cached)
            return
        }
        await requestHandler(
            () => axios.post('/api/insights/monthly-dashboard', { month }),
            (data) => {
                if (data) {
                    monthlyDataCache.current.set(month, data as MonthlyDashboardData)
                    setMonthlyDashboardData(data as MonthlyDashboardData)
                }
            },
            () => {},
            setLoadingMonthlyDashboard
        )
    }, [])

    const fetchStatusMetrics = useCallback(async (companySlug: string, start: string, end: string, force = false) => {
        if (companySlug !== 'biz2credit') {
            setStatusMetrics(null)
            setLoadingStatusMetrics(false)
            return
        }
        const key = `${companySlug}:${start}:${end}`
        const cached = statusMetricsCache.current.get(key)
        if (!force && cached) {
            setStatusMetrics(cached)
            return
        }
        setStatusMetrics(null)
        await requestHandler(
            () =>
                axios.get('/api/metrics/statusChange', {
                    params: { dateRange: JSON.stringify({ start, end }) },
                }),
            (data) => {
                if (data?.data) {
                    const metrics = data.data as Record<string, number>
                    statusMetricsCache.current.set(key, metrics)
                    setStatusMetrics(metrics)
                } else {
                    setStatusMetrics(null)
                }
            },
            () => setStatusMetrics(null),
            setLoadingStatusMetrics
        )
    }, [])

    const invalidatePredefinedCache = useCallback((sectionId?: string, start?: string, end?: string) => {
        if (!sectionId || !start || !end) {
            predefinedDataCache.current.clear()
            return
        }
        predefinedDataCache.current.delete(`${sectionId}:${start}:${end}`)
    }, [])

    const addDashboardTab = useCallback((id: string) => {
        setDashboardTabs((prev: string[]) => (prev.includes(id) ? prev : [...prev, id]))
    }, [])

    const renameDashboard = useCallback(
        async (dashboardId: string, name: string): Promise<boolean> => {
            const trimmed = name?.trim()
            if (!trimmed) return false
            const result = await requestHandler(
                () => axios.patch('/api/custom-analytics/dashboard', { dashboardId, name: trimmed }),
                (data) => {
                    setDashboardNames((prev) => ({ ...prev, [dashboardId]: trimmed }))
                },
                (errorMessage) => showErrorToast(errorMessage),
                null
            )
            return result !== undefined
        },
        [showErrorToast]
    )

    const deleteDashboard = useCallback(
        async (dashboardId: string): Promise<boolean> => {
            if (!dashboardId || dashboardId === 'default_section') return false

            // Remove charts associated with this dashboard
            const chartsToDelete = customCharts.filter((chart) => chart.dashboardId === dashboardId)
            const chartIdsToDelete = chartsToDelete.map((chart) => chart.id)

            // Delete charts first if there are any
            if (chartIdsToDelete.length > 0) {
                const deleteChartsResult = await requestHandler(
                    () => axios.post('/api/custom-analytics/remove-dashboard', { chartIds: chartIdsToDelete }),
                    () => {},
                    (errorMessage) => showErrorToast(errorMessage),
                    null
                )
                if (deleteChartsResult === undefined) return false
            }

            // Delete the dashboard
            const result = await requestHandler(
                () => axios.delete('/api/custom-analytics/dashboard', { data: { dashboardId } }),
                () => {
                    setDashboardTabs((prev) => prev.filter((id) => id !== dashboardId))
                    setDashboardNames((prev) => {
                        const newNames = { ...prev }
                        delete newNames[dashboardId]
                        return newNames
                    })
                    setCustomCharts((prev) => prev.filter((chart) => chart.dashboardId !== dashboardId))
                    // Clear cache
                    customChartsCache.current.clear()
                },
                (errorMessage) => showErrorToast(errorMessage),
                null
            )
            return result !== undefined
        },
        [customCharts, showErrorToast]
    )

    const value = useMemo(
        () => ({
            customCharts,
            dashboardTabs,
            dashboardNames,
            loadingCustomCharts,
            predefinedGraphsData,
            loadingPredefinedGraphs,
            monthlyDashboardData,
            loadingMonthlyDashboard,
            statusMetrics,
            loadingStatusMetrics,
            fetchCustomCharts,
            fetchPredefinedGraphs,
            fetchMonthlyDashboard,
            fetchStatusMetrics,
            invalidatePredefinedCache,
            addDashboardTab,
            renameDashboard,
            deleteDashboard,
        }),
        [
            customCharts,
            dashboardTabs,
            dashboardNames,
            loadingCustomCharts,
            predefinedGraphsData,
            loadingPredefinedGraphs,
            monthlyDashboardData,
            loadingMonthlyDashboard,
            statusMetrics,
            loadingStatusMetrics,
            fetchCustomCharts,
            fetchPredefinedGraphs,
            fetchMonthlyDashboard,
            fetchStatusMetrics,
            invalidatePredefinedCache,
            addDashboardTab,
            renameDashboard,
            deleteDashboard,
        ]
    )

    return <DashboardChartsContext.Provider value={value}>{children}</DashboardChartsContext.Provider>
}

export const useDashboardCharts = () => {
    const ctx = useContext(DashboardChartsContext)
    if (!ctx) throw new Error('useDashboardCharts must be used within DashboardChartsProvider')
    return ctx
}
